import { AnimatePresence, motion } from 'framer-motion';
import {
  AlarmClockPlus,
  BadgeCheck,
  Bolt,
  CircleHelp,
  Clock3,
  Cloud,
  CloudOff,
  Lightbulb,
  LoaderCircle,
  RotateCcw,
  Send,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { TeamAvatar } from '../components/team/Avatar';
import { Button } from '../components/ui/Button';
import { useCountdown } from '../hooks/useCountdown';
import { callFunction, errorMessage } from '../lib/callables';
import { cn } from '../lib/cn';
import { NOMINAL_CONTEST_DURATION_LABEL, powerUpRules, TEAM_QUESTION_COUNT, timeRules } from '../lib/arena-rules';
import { formatFaClock, formatFaNumber, formatFaText, toPersianDigits } from '../lib/format';
import type { ArenaEvent, PublicQuestion, TeamPublic, TeamState, TimedScoreBreakdown } from '../types/arena';

const difficultyLabel = { easy: 'آسان', medium: 'متوسط', hard: 'سخت' } as const;
const difficultyClasses = {
  easy: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  medium: 'bg-amber-50 text-amber-700 ring-amber-100',
  hard: 'bg-red-50 text-red-700 ring-red-100',
} as const;

interface Feedback {
  kind: 'success' | 'error' | 'power';
  title: string;
  detail: string;
}

interface SubmitResult {
  correct?: boolean;
  verdict: string;
  correctnessRatio?: number;
  scoreAwarded?: number;
  scoreBreakdown?: TimedScoreBreakdown;
  comparisonReason?: string;
  passedRequirements?: string[];
  failedRequirements?: string[];
  retryOffered?: boolean;
  unlocked?: string[];
}

function PowerButton({ icon, title, count, disabled, onClick }: { icon: React.ReactNode; title: string; count: number; disabled: boolean; onClick: () => void }) {
  return (
    <button disabled={disabled} onClick={onClick} className="group flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 p-3 text-right transition hover:border-brand/30 hover:bg-brand/[.06] disabled:opacity-35">
      <span className="grid size-9 place-items-center rounded-lg bg-white text-brand shadow-sm transition group-hover:bg-brand group-hover:text-white">{icon}</span>
      <span className="min-w-0 flex-1">
        <b className="block text-xs">{title}</b>
        <small className="text-[9px] text-slate-400">باقی‌مانده {formatFaNumber(count)}</small>
      </span>
      <span className="font-mono text-xs text-slate-400">×{formatFaNumber(count)}</span>
    </button>
  );
}

function scoreFeedback(scoreAwarded: number, breakdown?: TimedScoreBreakdown) {
  if (!breakdown) return `${formatFaNumber(scoreAwarded)} امتیاز به تیم اضافه شد.`;
  const speedPercent = Math.round(breakdown.speedRatio * 100);
  const multiplierLabel = breakdown.multiplier < 1 ? ` · ضریب راهنما/فرصت دوباره ${formatFaNumber(Math.round(breakdown.multiplier * 100))}٪` : '';
  return `${formatFaNumber(scoreAwarded)} امتیاز ترکیبی؛ سرعت پاسخ ${formatFaNumber(speedPercent)}٪${multiplierLabel}.`;
}

function queryProblemDetail(result: SubmitResult) {
  if (result.comparisonReason) return result.comparisonReason;
  const missing = result.failedRequirements?.slice(0, 4).join('، ');
  if (missing) return `مشکل Query: ${missing}`;
  const labels: Record<string, string> = {
    wrong_answer: 'Query با rubric این سؤال جور نشد.',
    wrong_query: 'Query معتبر برای این سؤال تشخیص داده نشد.',
    partial_query: 'Query ناقص است.',
    unsafe_query: 'فقط SELECT/CTE مجاز است.',
    empty_answer: 'Query کامل T-SQL را وارد کنید.',
  };
  return labels[result.verdict] ?? 'این تلاش بدون امتیاز ثبت شد.';
}

export function ArenaPage({ event, team, state, question, rank }: { event: ArenaEvent; team: TeamPublic; state: TeamState; question: PublicQuestion | null; rank: number }) {
  const [answer, setAnswer] = useState(state.draftAnswer || '');
  const [submitting, setSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'offline' | 'error'>('saved');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const questionId = state.currentQuestionId;
  const previousQuestion = useRef<string | null>(questionId);
  const expiredFor = useRef<string | null>(null);
  const draftTimer = useRef<number | null>(null);
  const remainingMs = useCountdown(state.deadlineMs, event.status === 'running');
  const totalQuestions = state.assignment.length || TEAM_QUESTION_COUNT;
  const routeProgressPercent = Math.min(100, Math.max(0, ((state.currentIndex + 1) / totalQuestions) * 100));
  const timeProgressPercent = Math.max(0, Math.min(100, question ? (remainingMs / (question.timeLimitSec * 1000)) * 100 : 0));
  const locked = event.status !== 'running' || !question || submitting || state.awaitingRetryDecision;
  const lowTime = remainingMs > 0 && remainingMs <= 30_000;

  useEffect(() => {
    if (questionId && previousQuestion.current !== questionId) {
      previousQuestion.current = questionId;
      expiredFor.current = null;
      setAnswer(state.draftAnswer || '');
      setHint(null);
    }
  }, [questionId, state.draftAnswer]);

  useEffect(() => {
    if (!questionId || answer === state.draftAnswer || event.status === 'finished') return;
    setSaveStatus(navigator.onLine ? 'saving' : 'offline');
    if (draftTimer.current !== null) window.clearTimeout(draftTimer.current);
    draftTimer.current = window.setTimeout(() => {
      if (!navigator.onLine) return;
      void callFunction<{ answer: string; questionId: string | null }, { savedAtMs: number; ignored?: boolean }>('updateDraft', { answer, questionId })
        .then(() => setSaveStatus('saved'))
        .catch(() => setSaveStatus('error'));
    }, 900);
    return () => {
      if (draftTimer.current !== null) window.clearTimeout(draftTimer.current);
      draftTimer.current = null;
    };
  }, [answer, event.status, questionId, state.draftAnswer]);

  useEffect(() => {
    const flush = () => {
      if (document.visibilityState === 'hidden' && answer !== state.draftAnswer) {
        void callFunction('updateDraft', { answer, questionId }).catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', flush);
    return () => document.removeEventListener('visibilitychange', flush);
  }, [answer, questionId, state.draftAnswer]);

  useEffect(() => {
    if (event.status === 'running' && questionId && remainingMs === 0 && expiredFor.current !== questionId) {
      expiredFor.current = questionId;
      void callFunction<Record<string, never>, { expired: boolean }>('expireQuestion', {})
        .then((result) => result.expired && setFeedback({ kind: 'error', title: 'زمان تمام شد', detail: 'این راند بدون امتیاز ثبت شد.' }))
        .catch(() => {
          expiredFor.current = null;
        });
    }
  }, [event.status, questionId, remainingMs]);

  const submit = async () => {
    if (draftTimer.current !== null) {
      window.clearTimeout(draftTimer.current);
      draftTimer.current = null;
    }
    setSubmitting(true);
    try {
      const result = await callFunction<
        { answer: string; submissionId: string },
        SubmitResult
      >('submitAnswer', {
        answer,
        submissionId: crypto.randomUUID(),
      });
      if (!result.retryOffered) {
        setAnswer('');
        setSaveStatus('saved');
      }
      if (result.correct) {
        const unlockNames: Record<string, string> = { hint: 'راهنما', extraTime: '۳۰ ثانیه اضافه', retry: 'فرصت دوباره' };
        const unlockedLabel = result.unlocked?.map((item) => unlockNames[item] ?? item).join(' و ');
        setFeedback(
          unlockedLabel
            ? { kind: 'power', title: 'جایزه آزاد شد!', detail: `${unlockedLabel} به کوله‌پشتی تیم اضافه شد.` }
            : { kind: 'success', title: 'درست بود؛ سرعت هم حساب شد!', detail: scoreFeedback(result.scoreAwarded ?? 0, result.scoreBreakdown) },
        );
      } else if (result.retryOffered) {
        setFeedback({ kind: 'power', title: 'یک جان اضافه دارید', detail: `پاسخ درست نبود؛ ${queryProblemDetail(result)} می‌توانید یک بار دیگر Query را اصلاح کنید.` });
      } else if ((result.scoreAwarded ?? 0) > 0) {
        const ratio = Math.round((result.correctnessRatio ?? 0) * 100);
        setFeedback({
          kind: 'power',
          title: 'Query ناقص بود، ولی امتیاز بخشی گرفتید',
          detail: `${formatFaNumber(result.scoreAwarded ?? 0)} امتیاز؛ پوشش rubric حدود ${formatFaNumber(ratio)}٪. ${queryProblemDetail(result)} سؤال بعدی شروع شد.`,
        });
      } else {
        setFeedback({ kind: 'error', title: 'این Query به هدف نخورد', detail: `${queryProblemDetail(result)} این تلاش ثبت شد و سؤال بعدی شروع شد.` });
      }
    } catch (reason) {
      setFeedback({ kind: 'error', title: 'ثبت پاسخ به مشکل خورد', detail: errorMessage(reason) });
    } finally {
      setSubmitting(false);
    }
  };

  const power = async (action: 'hint' | 'extra-time' | 'retry' | 'decline-retry') => {
    try {
      const result = await callFunction<{ action: string }, { hint?: string }>('usePowerUp', { action });
      if (action === 'hint' && result.hint) setHint(result.hint);
      if (action === 'extra-time') setFeedback({ kind: 'power', title: '۳۰ ثانیه اضافه شد', detail: 'زمان جدید ثبت شد؛ نفس تازه کنید و ادامه دهید.' });
      if (action === 'retry') setFeedback({ kind: 'power', title: 'فرصت دوباره فعال شد', detail: 'در تلاش دوم حداکثر ۵۰٪ امتیاز می‌گیرید.' });
      if (action === 'decline-retry') {
        setAnswer('');
        setFeedback(null);
      }
    } catch (reason) {
      setFeedback({ kind: 'error', title: 'جایزه فعال نشد', detail: errorMessage(reason) });
    }
  };

  return (
    <main className="dbx-aurora min-h-screen overflow-hidden text-white lg:h-screen" dir="rtl">
      <header className="flex h-[72px] items-center justify-between px-6">
        <div className="flex items-center gap-4">
          <BrandLockup inverse />
          <div className="hidden h-7 w-px bg-white/10 md:block" />
          <b className="hidden text-sm md:block">مسابقه دیتابینکس T-SQL</b>
        </div>
        <div className="flex items-center gap-3">
          <div className="hidden items-center gap-2 rounded-2xl border border-white/10 bg-white/[.055] px-3 py-2 text-[10px] text-white/55 sm:flex">
            {navigator.onLine ? <Cloud className="size-4 text-emerald-400" /> : <CloudOff className="size-4 text-red-400" />}
            {saveStatus === 'saving' ? 'در حال ذخیره…' : saveStatus === 'saved' ? 'Query ذخیره شد' : saveStatus === 'offline' ? 'منتظر اتصال' : 'خطا در ذخیره'}
          </div>
          <TeamAvatar avatarId={team.avatarId} color={team.color} className="size-10 rounded-xl" />
          <div className="min-w-0 text-left">
            <b className="block max-w-36 truncate text-sm">{team.name}</b>
            <span className="block text-[10px] text-white/40">رتبه شما: {formatFaNumber(rank)}</span>
          </div>
        </div>
      </header>

      <section className="mx-5 grid min-h-[94px] items-center gap-4 rounded-2xl border border-white/10 bg-white/[.055] px-6 py-4 shadow-2xl backdrop-blur-xl lg:grid-cols-[1.1fr_1fr_1fr_1fr]">
        <div>
          <span className="block text-xs text-white/45">وضعیت مسابقه</span>
          <b className="mt-2 flex items-center gap-2 text-2xl text-emerald-300">
            <span className="size-3 rounded-full bg-emerald-400 shadow-[0_0_16px_#34d399]" /> در حال برگزاری
          </b>
        </div>
        <div className="border-white/10 lg:border-r lg:pr-6">
          <span className="block text-xs text-white/45">زمان باقی‌مانده</span>
          <b className={cn('mt-1 block font-mono text-3xl tabular-nums', lowTime ? 'text-red-300' : 'text-white')} dir="ltr">{formatFaClock(remainingMs)}</b>
        </div>
        <div className="border-white/10 lg:border-r lg:pr-6">
          <span className="block text-xs text-white/45">نام تیم</span>
          <b className="mt-1 block truncate text-xl">{team.name}</b>
        </div>
        <div className="border-white/10 lg:border-r lg:pr-6">
          <span className="block text-xs text-white/45">امتیاز فعلی</span>
          <motion.b key={team.score} initial={{ scale: 1.25, color: '#28D7FF' }} animate={{ scale: 1, color: '#FFFFFF' }} className="mt-1 block font-mono text-3xl">
            {formatFaNumber(team.score)}
          </motion.b>
        </div>
      </section>

      <div className="grid gap-5 p-5 lg:h-[calc(100vh-186px)] lg:grid-cols-[minmax(0,1fr)_370px]" dir="ltr">
        <motion.section key={question?.id ?? 'loading'} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="flex min-h-[620px] min-w-0 flex-col overflow-hidden rounded-[28px] bg-white text-ink shadow-2xl lg:min-h-0" dir="rtl">
          {question ? (
            <>
              <div className="flex items-center justify-between border-b border-slate-200 px-7 py-5">
                <div className="flex items-center gap-3">
                  <span className={cn('rounded-xl px-3 py-1 text-[11px] font-black ring-1', difficultyClasses[question.difficulty])}>{difficultyLabel[question.difficulty]}</span>
                  <span className="rounded-xl bg-brand/10 px-3 py-1 text-[11px] font-black text-brand">{formatFaNumber(question.points)} امتیاز</span>
                </div>
                <span className="font-mono text-xs text-slate-400" dir="ltr">
                  سؤال {toPersianDigits(String(state.currentIndex + 1).padStart(2, '0'))} از {formatFaNumber(totalQuestions)}
                </span>
              </div>
              <div className="scrollbar-thin min-h-0 flex-1 overflow-y-auto p-7">
                <h1 className="text-3xl font-black leading-tight">{question.title}</h1>
                <QuestionPrompt prompt={question.prompt} />

                {hint && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-6 rounded-2xl border-r-2 border-amber-400 bg-amber-50 p-4 text-xs leading-6 text-amber-800">
                    <span className="mb-1 flex items-center gap-2 font-black text-amber-600">
                      <Lightbulb className="size-4" /> راهنما
                    </span>
                    {hint}
                  </motion.div>
                )}

                <div className="mt-7 overflow-hidden rounded-[24px] border border-slate-200 bg-white">
                  <div className="flex items-center justify-between border-b border-slate-200 bg-slate-50 px-5 py-4">
                    <div>
                      <span className="text-[10px] font-bold text-slate-400">SQL Server (T-SQL)</span>
                      <h2 className="mt-1 text-lg font-black">Query نهایی تیم</h2>
                    </div>
                    <span className="rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-black text-emerald-700">داوری متن Query</span>
                  </div>
                  <div className="p-5">
                    <textarea
                      dir="auto"
                      value={answer}
                      onChange={(event) => setAnswer(event.target.value)}
                      readOnly={locked}
                      placeholder="Query T-SQL را اینجا بنویسید"
                      className="h-[260px] w-full resize-none rounded-2xl border border-slate-200 bg-slate-50 p-5 text-left font-mono text-base leading-8 text-ink outline-none transition placeholder:text-slate-300 focus:border-brand/60 focus:bg-white disabled:opacity-50"
                    />
                  </div>
                  <div className="flex flex-wrap items-center justify-between gap-4 border-t border-slate-200 px-5 py-4">
                    <p className="max-w-xl text-[11px] leading-6 text-slate-500">فقط Query کامل T-SQL را بنویسید و نام ستون‌های خروجی را دقیقاً مطابق متن سؤال بگذارید.</p>
                    <Button size="lg" disabled={locked || submitting || !answer.trim()} onClick={() => void submit()}>
                      {submitting ? <LoaderCircle className="size-4 animate-spin" /> : <Send className="size-4" />} ثبت Query نهایی
                    </Button>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="grid h-full place-items-center">
              <LoaderCircle className="size-6 animate-spin text-brand" />
            </div>
          )}
        </motion.section>

        <aside className="scrollbar-thin min-h-[620px] overflow-y-auto rounded-[28px] bg-white text-ink shadow-2xl lg:min-h-0" dir="rtl">
          <div className={cn('m-5 rounded-2xl border p-5 text-center transition', lowTime ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-slate-50')}>
            <span className="flex items-center justify-center gap-2 text-xs text-slate-500">
              <Clock3 className="size-4" /> زمان سؤال جاری
            </span>
            <strong className={cn('mt-2 block font-mono text-4xl tabular-nums', lowTime ? 'text-red-500' : 'text-ink')} dir="ltr">
              {formatFaClock(remainingMs)}
            </strong>
            <div className="mt-4 h-1.5 overflow-hidden rounded bg-slate-200">
              <motion.div className={cn('h-full origin-left', lowTime ? 'bg-red-500' : 'bg-brand')} animate={{ width: `${timeProgressPercent}%` }} />
            </div>
          </div>

          <div className="border-y border-slate-200 p-5">
            <h2 className="text-lg font-black">مسیر سؤالات</h2>
            <div className="mt-4 grid grid-cols-4 gap-3">
              {Array.from({ length: totalQuestions }, (_, index) => (
                <span
                  key={index}
                  className={cn(
                    'grid size-11 place-items-center rounded-xl border text-sm font-black transition',
                    index < state.currentIndex && 'border-emerald-200 bg-emerald-500 text-white',
                    index === state.currentIndex && 'border-brand bg-brand text-white shadow-glow',
                    index > state.currentIndex && 'border-slate-200 bg-white text-slate-500',
                  )}
                >
                  {formatFaNumber(index + 1)}
                </span>
              ))}
            </div>
          </div>

          <div className="border-b border-slate-200 p-5">
            <h3 className="text-lg font-black">اطلاعات سؤال جاری</h3>
            <div className="mt-4 divide-y divide-slate-200 rounded-2xl border border-slate-200">
              <InfoRow label="سختی" value={question ? difficultyLabel[question.difficulty] : '—'} />
              <InfoRow label="امتیاز" value={question ? `${formatFaNumber(question.points)} امتیاز` : '—'} />
              <InfoRow label="زنجیره درست‌ها" value={`×${formatFaNumber(team.streak)}`} />
            </div>
          </div>

          <div className="border-b border-slate-200 p-5">
            <h3 className="text-lg font-black">پیشرفت شما</h3>
            <div className="mt-4 flex items-center gap-4">
              <div className="grid size-20 place-items-center rounded-full border-[8px] border-brand/20 bg-brand/10">
                <b className="font-mono text-xl text-brand">{formatFaNumber(Math.round(routeProgressPercent))}٪</b>
              </div>
              <div>
                <b className="block">{formatFaNumber(Math.min(state.currentIndex + 1, totalQuestions))} از {formatFaNumber(totalQuestions)} سؤال</b>
                <span className="text-xs text-slate-400">پاسخ‌ها بعد از ثبت قفل می‌شوند</span>
              </div>
            </div>
          </div>

          <div className="p-5">
            <span className="mb-3 block text-[10px] font-black tracking-wider text-slate-400">جایزه‌ها</span>
            <div className="space-y-2">
              <PowerButton icon={<Lightbulb className="size-4" />} title="راهنما" count={state.powerUps.hint} disabled={locked || state.powerUps.hint < 1 || state.usedHint} onClick={() => void power('hint')} />
              <PowerButton icon={<AlarmClockPlus className="size-4" />} title="۳۰ ثانیه اضافه" count={state.powerUps.extraTime} disabled={locked || state.powerUps.extraTime < 1} onClick={() => void power('extra-time')} />
              <PowerButton icon={<RotateCcw className="size-4" />} title="فرصت دوباره" count={state.powerUps.retry} disabled={!state.awaitingRetryDecision || state.powerUps.retry < 1} onClick={() => void power('retry')} />
            </div>
            <div className="mt-5 space-y-2 text-[10px] leading-5 text-slate-500">
              {powerUpRules.map((item) => (
                <p key={item.title}><b className="text-ink">{item.title}:</b> {item.detail}</p>
              ))}
            </div>
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <span className="text-[9px] font-bold text-slate-400">زمان‌بندی</span>
              <p className="mt-2 text-[11px] leading-5 text-slate-600">{formatFaNumber(TEAM_QUESTION_COUNT)} سؤال، زمان اسمی کل {NOMINAL_CONTEST_DURATION_LABEL} دقیقه.</p>
              <div className="mt-3 grid grid-cols-3 gap-2">
                {timeRules.map((item) => (
                  <div key={item.label} className="rounded-lg bg-white px-2 py-2">
                    <span className="block text-[9px] text-slate-400">{item.label}</span>
                    <b className="mt-1 block text-[10px]">{item.value}</b>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </aside>
      </div>

      <AnimatePresence>
        {event.status === 'countdown' && <CountdownOverlay startsAtMs={event.startsAtMs} />}
        {event.status === 'paused' && <PauseOverlay connection={event.pausedReason === 'connection'} />}
        {state.awaitingRetryDecision && (
          <div className="fixed inset-x-0 bottom-5 z-50 mx-auto flex w-fit items-center gap-4 rounded-2xl border border-amber-400/25 bg-[#171B25]/95 p-4 shadow-2xl backdrop-blur">
            <CircleHelp className="size-6 text-amber-300" />
            <div>
              <b className="block text-sm">یک شانس دیگر می‌خواهید؟</b>
              <span className="text-[10px] text-white/45">تلاش دوم تا ۵۰٪ امتیاز دارد.</span>
            </div>
            <Button size="sm" onClick={() => void power('retry')}>
              بله، دوباره می‌زنیم
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void power('decline-retry')}>
              نه، سؤال بعدی
            </Button>
          </div>
        )}
        {feedback && (
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 20 }} className={cn('fixed bottom-6 left-1/2 z-[60] flex min-w-[360px] -translate-x-1/2 items-center gap-4 rounded-2xl border bg-[#151C2A]/95 p-4 shadow-2xl backdrop-blur', feedback.kind === 'success' ? 'border-emerald-400/30' : feedback.kind === 'power' ? 'border-amber-400/30' : 'border-red-400/25')}>
            <span className={cn('grid size-11 place-items-center rounded-xl', feedback.kind === 'success' ? 'bg-emerald-400/12 text-emerald-300' : feedback.kind === 'power' ? 'bg-amber-400/12 text-amber-300' : 'bg-red-400/12 text-red-300')}>
              {feedback.kind === 'success' ? <BadgeCheck /> : feedback.kind === 'power' ? <Bolt /> : <X />}
            </span>
            <div className="flex-1">
              <b className="block text-sm">{feedback.title}</b>
              <span className="mt-1 block text-[10px] text-white/50">{feedback.detail}</span>
            </div>
            <button onClick={() => setFeedback(null)} className="text-white/30 hover:text-white">
              <X className="size-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3 text-xs">
      <span className="text-slate-400">{label}</span>
      <b className="truncate text-ink" dir="auto">{value}</b>
    </div>
  );
}

function CountdownOverlay({ startsAtMs }: { startsAtMs: number | null }) {
  const remaining = useCountdown(startsAtMs, true);
  const count = Math.max(1, Math.ceil(remaining / 1000));
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 grid place-items-center bg-ink/88 backdrop-blur-md">
      <motion.div key={count} initial={{ scale: 0.4, opacity: 0, rotate: -8 }} animate={{ scale: 1, opacity: 1, rotate: 0 }} exit={{ scale: 1.6, opacity: 0 }} className="relative text-center">
        <span className="absolute inset-1/2 size-52 -translate-x-1/2 -translate-y-1/2 rounded-full border border-brand/25" />
        <strong className="relative font-mono text-[9rem] font-black leading-none text-brand">{formatFaNumber(count)}</strong>
        <p className="mt-7 text-sm font-bold text-white/55">آماده باشید؛ بازی از همین لحظه شروع می‌شود</p>
      </motion.div>
    </motion.div>
  );
}

function QuestionPrompt({ prompt }: { prompt: string }) {
  const segments: Array<{ kind: 'text' | 'table'; value: string }> = [];
  for (const line of prompt.split('\n')) {
    const kind = line.includes('|') ? 'table' : 'text';
    const previous = segments.at(-1);
    if (previous?.kind === kind) {
      previous.value += `\n${line}`;
    } else {
      segments.push({ kind, value: line });
    }
  }

  return (
    <div className="mt-4 space-y-4">
      {segments.map((segment, index) => {
        const value = segment.value.trim();
        if (!value) return null;
        if (segment.kind === 'table') {
          return (
            <pre key={index} dir="ltr" className="overflow-x-auto rounded-2xl border border-white/10 bg-[#080D17] p-4 text-left font-mono text-[12px] leading-7 text-slate-200 shadow-inner scrollbar-thin">
              {formatFaText(value)}
            </pre>
          );
        }
        return (
          <p key={index} className="whitespace-pre-line text-sm leading-8 text-slate-600">
            {formatFaText(value)}
          </p>
        );
      })}
    </div>
  );
}

function PauseOverlay({ connection }: { connection: boolean }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-40 grid place-items-center bg-ink/74 backdrop-blur-sm">
      <div className="max-w-lg text-center">
        <span className="mx-auto grid size-16 place-items-center rounded-2xl border border-brand/25 bg-brand/10">
          <CloudOff className="size-7 text-brand" />
        </span>
        <h2 className="mt-6 text-2xl font-black">بازی موقتاً مکث شد</h2>
        <p className="mt-3 leading-7 text-slate-300">{connection ? 'یکی از تیم‌ها از خط خارج شده است؛ بازی تا برگشت تیم مکث می‌ماند.' : 'مدیر مسابقه بازی را مکث کرده است. Query و زمان شما محفوظ است.'}</p>
        <span className="mt-6 inline-flex items-center gap-2 text-xs text-white/40">
          <LoaderCircle className="size-4 animate-spin" /> منتظر سوت ادامه
        </span>
      </div>
    </motion.div>
  );
}
